import { ContactChannelData } from "./types";

export interface NameParts {
	firstName: string;
	middleName: string;
	lastName: string;
}

/**
 * Split a full name into the three editable parts: the first token is the first
 * name, the last token the last name and everything in between the middle name.
 */
const splitName = (name: string): NameParts => {
	const words = name.split(/\s+/).filter(Boolean);

	if (words.length === 0) {
		return { firstName: "", middleName: "", lastName: "" };
	}

	if (words.length === 1) {
		return { firstName: words[0], middleName: "", lastName: "" };
	}

	return {
		firstName: words[0],
		middleName: words.slice(1, -1).join(" "),
		lastName: words[words.length - 1],
	};
};

const joinParts = ({ firstName, middleName, lastName }: NameParts): string =>
	[firstName, middleName, lastName].filter(Boolean).join(" ");

/**
 * Name parts to prefill the edit modal with.
 *
 * The stored parts are frequently incomplete: prospects imported from CSV or
 * LinkedIn keep the full `name` but get their parts from a crude "first word +
 * last word" split, so "Dr. Anna Maria von Müller" is stored as `Anna` / `Müller`
 * with no middle name. Since saving recomputes `name` from the parts, prefilling
 * those stored values would silently drop the tokens in between (PREC-3759).
 *
 * So whenever the stored parts do not reconstruct `name`, derive them from the
 * full name instead — the user then sees the complete name and saving preserves it.
 */
export const deriveNameParts = (data: ContactChannelData): NameParts => {
	const stored: NameParts = {
		firstName: data.firstName ?? "",
		middleName: data.middleName ?? "",
		lastName: data.lastName ?? "",
	};

	const name = (data.name ?? "").trim();

	if (!name || joinParts(stored) === name) {
		return stored;
	}

	return splitName(name);
};
